import { resetComponent } from '../../style';
import { genStyleHooks } from '../../theme/internal';
import type { AliasToken, GenerateStyle } from '../../theme/internal';
import { blue, green, purple, red, yellow } from '../../theme/themes/colorPalette';

// biome-ignore lint/suspicious/noEmptyInterface: ComponentToken need to be empty by default
export interface ComponentToken {}

export interface WarningBannerToken extends AliasToken, ComponentToken {
  componentCls: string;
}

export const initWarningBannerTriggerToken = (token: WarningBannerToken): WarningBannerToken => ({
  ...token,
  componentCls: '.youlify-warning-banner',
});

const genWarningBannerPropsStyle: GenerateStyle<WarningBannerToken> = (token) => {
  const { componentCls } = token;

  return {
    [componentCls]: {
      ...resetComponent(token),
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      padding: 16,
      borderRadius: 4,
      fontSize: 14,
      fontWeight: 500,

      /* icon */
      [`& ${componentCls}-icon`]: {
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
      },

      [`&${componentCls}-without-icon`]: {
        padding: '16px 32px',
      },

      /* content */
      [`& ${componentCls}-content`]: {
        flex: 1,
        textAlign: 'left',
      },

      [`& ${componentCls}-content-items`]: {
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginXXS,
      },

      [`& ${componentCls}-content-rows`]: {
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginXXS,
      },

      [`& ${componentCls}-content-row`]: {
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      },

      [`& ${componentCls}-content-item`]: {
        lineHeight: token.lineHeight,
      },

      [`& ${componentCls}-list`]: {
        margin: `${token.marginXXS}px 0 0`,
        paddingLeft: token.marginLG,
        listStyleType: 'disc',
        listStylePosition: 'outside',
      },

      [`& ${componentCls}-list-item`]: {
        display: 'list-item',
        lineHeight: token.lineHeight,
        listStyleType: 'inherit',
      },

      [`& ${componentCls}-title`]: {
        fontWeight: 600,
        marginRight: token.marginXXS,
      },

      [`& ${componentCls}-desc`]: {
        wordBreak: 'break-all',
      },

      [`&${componentCls}-info`]: {
        background: purple.light2,
        color: purple.dark,
      },

      [`&${componentCls}-success`]: {
        background: green.light2,
        color: green.dark,
      },

      [`&${componentCls}-warning`]: {
        background: yellow.light2,
        color: yellow.dark,
      },

      [`&${componentCls}-error`]: {
        background: red.light2,
        color: red.dark,
      },

      [`&${componentCls}-secondaryInfo`]: {
        background: blue[100],
        color: blue[400],
      },

      [`&${componentCls}-warn_pink`]: {
        background: '#FFE8F1',
        color: '#CB1E83',
      },
    },
  };
};

export const useWarningBannerStyle = genStyleHooks(
  'WarningBanner',
  (token) => [genWarningBannerPropsStyle(initWarningBannerTriggerToken(token))],
  undefined,
  {
    resetFont: false,
  },
);
